import React, { useRef, useState, useCallback, useMemo, useEffect, memo } from 'react';
import { useNavigate } from 'react-router-dom';
import PlayIcon from '../icons/PlayIcon';
import BoltIcon from '../icons/BoltIcon';
import { Icon, IconName } from '@hills-design-system/components';
import { usePetParentReportActions } from '../../hooks/usePetParentReports';
import { CheckCircleIcon } from '../icons';
import { analytics, WelcomeFlowTracking } from '../../services/analytics';

const AUTOMATED_ICON_PLACEHOLDER = '[automated_icon]' as const;
const MODAL_SIZES = {
  WITH_MEDIA: 'w-[846px] max-w-[90vw] max-h-[90vh]',
  WITHOUT_MEDIA: 'w-[846px] h-[700px] max-w-[90vw]',
} as const;

interface CmsFile {
  url: string;
  title?: string;
  description?: string;
}

interface CmsHpsfButton {
  text: string;
  url: string;
  variant: 'filled' | 'outlined';
  inverted: boolean;
  icon: string;
  icon_position: string | null;
  external: boolean;
}

interface CmsHpsfHeroBannerItem {
  image: CmsFile | null;
  video?: CmsFile | null;
  headline: string;
  body: string;
  buttons: CmsHpsfButton[];
}

interface CmsHpsfHeroBannerData {
  name?: string;
  component?: string;
  items: CmsHpsfHeroBannerItem[];
  pagination?: boolean;
  navigation?: boolean;
  scroll_bar?: boolean;
  loop?: boolean;
  auto_play_delay?: number;
}

interface CmsHpsfHeroBannerProps {
  data: CmsHpsfHeroBannerData;
  onClose?: () => void;
  videoOnly?: boolean;
  welcomeFlowTracking?: WelcomeFlowTracking;
}

const VideoMedia = memo<{ video: CmsFile; poster?: string }>(({ video, poster }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);

  const handlePlay = useCallback(() => setIsPlaying(true), []);
  const handlePause = useCallback(() => setIsPlaying(false), []);

  const handleOverlayPlay = useCallback(() => {
    videoRef.current?.play().catch(() => {});
  }, []);

  return (
    <div className="relative w-full aspect-video cursor-pointer overflow-hidden rounded-t-800 min-h-0 flex-shrink">
      <video
        ref={videoRef}
        src={video.url}
        poster={poster}
        controls
        disablePictureInPicture
        playsInline
        preload="metadata"
        className="absolute inset-0 w-full h-full object-cover"
        onPlay={handlePlay}
        onPause={handlePause}
        onContextMenu={(e) => e.preventDefault()}
      >
        <track kind="captions" />
      </video>
      {!isPlaying && (
        <button
          onClick={handleOverlayPlay}
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-black/60 hover:bg-black/70 rounded-full w-[72px] h-[72px] flex items-center justify-center transition-all z-20 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-black/60"
          aria-label="Play video"
        >
          <PlayIcon width={36} height={36} />
        </button>
      )}
    </div>
  );
});

VideoMedia.displayName = 'VideoMedia';

const ImageMedia = memo<{ image: CmsFile }>(({ image }) => (
  <div className="w-full flex items-center justify-center rounded-t-800 bg-blue-0 pt-[51px] pb-[50px] px-[123px] min-h-0 flex-shrink overflow-hidden">
    <img
      src={image.url}
      alt={image.title ?? image.description ?? ''}
      className="w-full flex-shrink-0 object-cover bg-gray-100"
      style={{ aspectRatio: '8/5' }}
    />
  </div>
));

ImageMedia.displayName = 'ImageMedia';

const CloseButton = memo<{ onClick: () => void }>(({ onClick }) => (
  <button
    onClick={onClick}
    aria-label="Close modal (Escape key)"
    className="absolute top-200 right-200 z-30 w-[48px] h-[48px] p-50 flex items-center justify-center flex-shrink-0 rounded-full bg-gray-50 shadow-100 hover:bg-gray-100 transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
  >
    <svg width="32" height="32" viewBox="0 0 32 32" fill="none" aria-hidden="true">
      <path d="M24 8L8 24M8 8L24 24" stroke="#0054A4" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  </button>
));

CloseButton.displayName = 'CloseButton';

export const CmsHpsfHeroBanner: React.FC<CmsHpsfHeroBannerProps> = ({ data, onClose, videoOnly = false, welcomeFlowTracking }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const navigate = useNavigate();
  const { downloadOptinGuide } = usePetParentReportActions();
  const { items } = data;
  const totalSlides = items.length;
  const currentItem = items[currentIndex];
  const modalRef = useRef<HTMLDivElement>(null);
  const quizStartFired = useRef(false);
  const quizEndFired = useRef(false);

  // Focus modal container on mount for screen reader accessibility
  useEffect(() => {
    modalRef.current?.focus();
  }, []);

  // Track welcome flow quiz start on mount (once per session)
  useEffect(() => {
    if (welcomeFlowTracking && totalSlides > 0 && !quizStartFired.current) {
      analytics.quizStart(welcomeFlowTracking.quizName);
      quizStartFired.current = true;
    }
  }, [welcomeFlowTracking]);

  // Track welcome flow quiz end when landing on last slide (once per session)
  useEffect(() => {
    if (welcomeFlowTracking && currentIndex === totalSlides - 1 && !quizEndFired.current) {
      analytics.quizEnd(welcomeFlowTracking.quizName);
      quizEndFired.current = true;
    }
  }, [currentIndex, totalSlides, welcomeFlowTracking]);

  const handleBack = useCallback(() => {
    if (currentIndex > 0) {
      setCurrentIndex((prev) => prev - 1);
    }
  }, [currentIndex]);

  const handleClose = useCallback(() => {
    if (onClose) {
      onClose();
    } else {
      navigate('/home');
    }
  }, [navigate, onClose]);

  const handleNext = useCallback(() => {
    if (welcomeFlowTracking) {
      const questionName = welcomeFlowTracking.questionNames[currentIndex];
      if (questionName) {
        analytics.quizAnswer(welcomeFlowTracking.quizName, questionName, 'next');
      }
    }

    if (currentIndex < totalSlides - 1) {
      setCurrentIndex((prev) => prev + 1);
    } else {
      handleClose();
    }
  }, [currentIndex, totalSlides, handleClose, welcomeFlowTracking]);

  const handleButtonClick = useCallback(
    (button: CmsHpsfButton) => {
      if (button.icon === 'download') {
        downloadOptinGuide();
        return;
      }
      
      if (button.url) {
        button.external
          ? globalThis.open(button.url, '_blank', 'noopener,noreferrer')
          : navigate(button.url);
      } else {
        handleNext();
      }
    },
    [handleNext, navigate, downloadOptinGuide]
  );

  // Memoize computed values
  const mediaState = useMemo(() => {
    const hasVideo = !!currentItem?.video?.url;
    const hasImage = !!currentItem?.image?.url;
    return {
      hasVideo,
      hasImage,
      hasMedia: hasVideo || hasImage,
    };
  }, [currentItem?.video?.url, currentItem?.image?.url]);

  // Memoize processed headline with icon flag
  const headlineData = useMemo(() => {
    if (!currentItem?.headline) return { text: '', hasIcon: false };
    
    const hasIcon = currentItem.headline.includes(AUTOMATED_ICON_PLACEHOLDER);
    const text = hasIcon
      ? currentItem.headline.replace(AUTOMATED_ICON_PLACEHOLDER, '').trim()
      : currentItem.headline;
    
    return { text, hasIcon };
  }, [currentItem?.headline]);

  // Memoize modal size class
  const modalSizeClass = useMemo(
    () => (mediaState.hasMedia ? MODAL_SIZES.WITH_MEDIA : MODAL_SIZES.WITHOUT_MEDIA),
    [mediaState.hasMedia]
  );

  // Memoize content section classes
  const contentSectionClass = useMemo(
    () =>
      mediaState.hasMedia
        ? 'flex flex-col sm:flex-row justify-between items-start p-200 gap-100 flex-shrink-0'
        : 'flex flex-col items-center justify-center text-center px-200 py-300 gap-100 flex-1',
    [mediaState.hasMedia]
  );

  // Memoize headline classes
  const headlineClass = useMemo(
    () =>
      `font-bold leading-detail m-0 flex items-center gap-40 ${
        mediaState.hasMedia ? 'text-detail-150 text-[#20252E]' : 'text-[32px] text-[#0054A4] justify-center'
      }`,
    [mediaState.hasMedia]
  );

  // Memoize text content classes
  const textContentClass = useMemo(
    () => `flex flex-col ${mediaState.hasMedia ? 'sm:max-w-[563px] flex-1 min-w-0 gap-75' : 'max-w-[450px] min-w-0 gap-75'}`,
    [mediaState.hasMedia]
  );

  // Memoize pagination section classes
  const paginationSectionClass = useMemo(
    () =>
      `flex-shrink-0 ${
        mediaState.hasMedia
          ? 'flex flex-row sm:flex-col items-end justify-between self-stretch sm:ml-200 gap-100'
          : 'flex flex-col items-center gap-100'
      }`,
    [mediaState.hasMedia]
  );

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleClose();
      } else if (e.key === 'ArrowRight') {
        handleNext();
      } else if (e.key === 'ArrowLeft') {
        handleBack();
      }
    };

    globalThis.addEventListener('keydown', handleKeyDown);
    return () => globalThis.removeEventListener('keydown', handleKeyDown);
  }, [handleClose, handleNext, handleBack]);

  if (!currentItem || totalSlides === 0) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
    >
      <div
        ref={modalRef}
        tabIndex={-1}
        className={`relative bg-white rounded-800 border border-gray-100 shadow-100 flex flex-col overflow-hidden focus:outline-none !h-auto !max-h-[90vh] ${modalSizeClass}`}
        role="dialog"
        aria-modal="true"
        aria-label="Welcome tutorial"
      >
        {/* Back button */}
        {currentIndex > 0 && (
          <button
            onClick={handleBack}
            aria-label="Back"
            className="absolute top-200 left-200 z-30 w-[48px] h-[48px] p-50 flex items-center justify-center flex-shrink-0 rounded-full bg-gray-50 shadow-100 hover:bg-gray-100 transition-colors"
            style={{ position: 'absolute' }}
          >
            <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
              <path d="M20 8L12 16L20 24" stroke="#0054A4" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        )}

        {/* Close button */}
        <button
          onClick={handleClose}
          aria-label="Close"
          className="absolute top-200 right-200 z-30 w-[48px] h-[48px] p-50 flex items-center justify-center flex-shrink-0 rounded-full bg-gray-50 shadow-100 hover:bg-gray-100 transition-colors"
          style={{ position: 'absolute' }}
        >
          <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
            <path d="M24 8L8 24M8 8L24 24" stroke="#0054A4" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>

        {/* Media section */}
        {mediaState.hasVideo && currentItem.video ? (
          <VideoMedia video={currentItem.video} poster={currentItem.image?.url} />
        ) : mediaState.hasImage && currentItem.image ? (
          <ImageMedia image={currentItem.image} />
        ) : null}

        {/* Content section */}
        {!videoOnly && (
        <div className={contentSectionClass}>
          {/* Icon for no-media case */}
          {!mediaState.hasMedia && (
            <div className="flex items-center justify-center" role="img" aria-label="Success checkmark icon">
             <CheckCircleIcon className="text-primary" width={56} height={56} />
            </div>
          )}

          {/* Headline + body */}
          <div className={textContentClass}>
            {headlineData.text && (
              <h2 className={headlineClass}>
                {headlineData.hasIcon && (
                  <BoltIcon
                    width={mediaState.hasMedia ? 24 : 32}
                    height={mediaState.hasMedia ? 24 : 32}
                    fill={mediaState.hasMedia ? '#20252E' : '#0054A4'}
                  />
                )}
                {headlineData.text}
              </h2>
            )}
            {currentItem.body && (
              <p className="text-detail-100 font-medium leading-detail text-gray-675 m-0">
                {currentItem.body}
              </p>
            )}
          </div>

          {/* Pagination + buttons */}
          <div className={paginationSectionClass}>
            {mediaState.hasMedia && data.pagination && totalSlides > 1 && (
              <span
                className="text-action-100 font-semibold leading-action text-primary whitespace-nowrap"
                role="status"
                aria-live="polite"
                aria-label={`Slide ${currentIndex + 1} of ${totalSlides}`}
              >
                {currentIndex + 1} of {totalSlides}
              </span>
            )}
            {mediaState.hasMedia && <div className="hidden sm:block flex-1" />}
            {currentItem.buttons.length > 0 && (
              <div className={`flex ${currentItem.buttons.length >= 2 ? 'flex-col gap-[13px]' : 'gap-40'}`}>
                {currentItem.buttons.map((button, index) => (
                  <button
                    key={`${button.text}-${index}`}
                    onClick={() => handleButtonClick(button)}
                    aria-label={button.icon === 'download' ? `${button.text} (downloads PDF)` : button.text}
                    className={`h-[52px] min-w-[100px] px-125 py-100 rounded-200 font-semibold text-action-100 leading-action transition-colors flex items-center justify-center gap-40 whitespace-nowrap focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                      button.variant === 'outlined'
                        ? 'border-2 border-primary text-primary bg-white hover:bg-secondary-hover focus:ring-primary'
                        : 'bg-primary text-white hover:bg-primary-hover focus:ring-primary'
                    }`}
                  >
                    {button.icon === 'download' && (
                      <Icon icon={IconName.Download} size={20} />
                    )}
                    {button.text}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
        )}
      </div>
    </div>
  );
};

export default CmsHpsfHeroBanner;